<template>
  <div class="review-container">
    <h2 class="text-4xl md:text-5xl font-bold mb-12 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
      {{ t('reviews.title') }}
    </h2>
    
    <!-- 提示消息 -->
    <div v-if="notification.show" 
         :class="[
           'fixed top-4 right-4 p-6 rounded-xl shadow-2xl max-w-md transition-all duration-500 transform',
           notification.type === 'success' ? 'bg-green-50 text-green-800 border-l-4 border-green-500' :
           notification.type === 'error' ? 'bg-red-50 text-red-800 border-l-4 border-red-500' : ''
         ]"
         style="z-index: 1000;">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <!-- 成功图标 -->
          <svg v-if="notification.type === 'success'" class="h-6 w-6 text-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          <!-- 错误图标 -->
          <svg v-if="notification.type === 'error'" class="h-6 w-6 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div class="ml-4">
          <p class="text-base font-medium">{{ notification.message }}</p>
        </div>
        <!-- 关闭按钮 -->
        <div class="ml-auto pl-3">
          <button
            class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none transform hover:scale-110 transition-all duration-300"
            @click="closeNotification"
          >
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- 评分部分 -->
    <div class="bg-white rounded-3xl shadow-xl p-8 md:p-10 mb-12 transform hover:scale-[1.02] transition-all duration-500">
      <div class="flex flex-col items-center space-y-6">
        <div class="flex items-center space-x-3">
          <template v-for="i in 5" :key="i">
            <button
              @click="newReview.rating = i"
              @mouseenter="hoverRating = i"
              @mouseleave="hoverRating = 0"
              class="star-button focus:outline-none relative group"
              :class="{'text-yellow-400': i <= (hoverRating || newReview.rating), 'text-gray-200': i > (hoverRating || newReview.rating)}"
            >
              <span class="relative z-10">★</span>
              <span 
                v-if="i <= (hoverRating || newReview.rating)"
                class="absolute inset-0 bg-yellow-400 opacity-20 rounded-full blur-lg transform scale-150 group-hover:scale-200 transition-transform duration-500"
              ></span>
            </button>
          </template>
        </div>
        
        <textarea
          v-model="newReview.comment"
          :placeholder="t('reviews.commentPlaceholder')"
          rows="4"
          class="w-full p-6 border-2 border-gray-100 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 resize-none text-lg transition-all duration-300 hover:border-gray-200"
          :disabled="isSubmitting"
        ></textarea>
        
        <button
          @click="submitReview"
          class="submit-button text-white relative group"
          :disabled="isSubmitting"
        >
          <span :class="{'opacity-0': isSubmitting}" class="relative z-10">{{ t('reviews.submit') }}</span>
          <!-- 加载动画 -->
          <div v-if="isSubmitting" class="absolute inset-0 flex items-center justify-center">
            <svg class="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <div class="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl"></div>
        </button>
      </div>
    </div>

    <!-- 评论列表 -->
    <div class="space-y-8">
      <div class="review-list custom-scrollbar" ref="reviewListRef">
        <div 
          v-for="review in visibleReviews" 
          :key="review.id" 
          class="review-card"
          :style="{
            transform: `translateY(${review.offsetY}px)`,
            position: 'absolute',
            left: '0',
            right: '0',
            width: '100%'
          }"
        >
          <div class="flex items-start space-x-4">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xl shadow-lg transform hover:scale-110 transition-all duration-300">
                {{ review.content?.[0]?.toUpperCase() || '?' }}
              </div>
            </div>
            <div class="flex-grow">
              <div class="flex items-center space-x-2 mb-2">
                <div class="flex space-x-1">
                  <span v-for="i in 5" :key="i" 
                    :class="[
                      'text-2xl transition-all duration-300 transform',
                      i <= review.rating ? 'text-yellow-400 scale-110' : 'text-gray-200'
                    ]"
                  >★</span>
                </div>
                <span class="text-gray-500 text-sm font-medium ml-2">
                  {{ formatDate(review.createdAt) }}
                </span>
              </div>
              <p class="text-gray-700 text-base leading-relaxed break-words">{{ review.content }}</p>
            </div>
          </div>
        </div>
        
        <!-- 加载更多按钮 -->
        <div v-if="hasMoreReviews && !loading" class="text-center mt-12" :style="{ transform: `translateY(${reviews.length * REVIEW_HEIGHT}px)` }">
          <button 
            @click="loadMoreReviews"
            class="px-8 py-4 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-md hover:shadow-xl transform hover:scale-105 font-medium"
          >
            {{ t('reviews.loadMore') }}
          </button>
        </div>
        
        <!-- 加载中状态 -->
        <div v-if="loading" class="text-center py-12" :style="{ transform: `translateY(${reviews.length * REVIEW_HEIGHT}px)` }">
          <div class="inline-block animate-bounce">
            <svg class="icon icon--loading w-10 h-10 text-blue-500">
              <use xlink:href="#icon-loading"></use>
            </svg>
          </div>
          <p class="text-gray-500 mt-4 text-lg">{{ t('reviews.loading') }}</p>
        </div>
        
        <!-- 无评论时显示 -->
        <div v-if="reviews.length === 0 && !loading" class="text-center py-20 px-8 bg-white rounded-3xl shadow-xl">
          <div class="text-7xl mb-6 animate-bounce">💭</div>
          <p class="text-gray-600 text-xl">{{ t('reviews.noReviews') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { getFirestore, collection, getDocs, addDoc, query, orderBy, serverTimestamp, limit, startAfter } from 'firebase/firestore'
import { ensureFirebaseInitialized } from '../firebase/config'

const { t } = useI18n()
const reviews = ref([])
const isSubmitting = ref(false)
const hoverRating = ref(0)
const newReview = ref({
  rating: 0,
  comment: ''
})
const loading = ref(true)
const error = ref(null)
const reviewListRef = ref(null)
const lastVisible = ref(null)
const hasMoreReviews = ref(true)
const PAGE_SIZE = 10
const REVIEW_HEIGHT = 150
const notification = ref({
  show: false,
  type: '',
  message: ''
})

// 显示通知
const showNotification = (type, message) => {
  notification.value = {
    show: true,
    type,
    message
  }
  setTimeout(() => {
    notification.value.show = false
  }, 3000)
}

// 关闭通知
const closeNotification = () => {
  notification.value.show = false
}

// 格式化日期
const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = timestamp.toDate()
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

// 加载评论
const loadReviews = async (isInitial = false) => {
  try {
    loading.value = true
    const { db } = await ensureFirebaseInitialized()
    
    if (!db) {
      throw new Error('Firebase未初始化')
    }

    const reviewsRef = collection(db, 'reviews')
    let q = query(
      reviewsRef,
      orderBy('createdAt', 'desc'),
      limit(PAGE_SIZE)
    )

    if (!isInitial && lastVisible.value) {
      q = query(
        reviewsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastVisible.value),
        limit(PAGE_SIZE)
      )
    }

    const querySnapshot = await getDocs(q)
    
    if (querySnapshot.empty) {
      hasMoreReviews.value = false
      if (isInitial) {
        reviews.value = []
      }
      return
    }

    const newReviews = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))

    lastVisible.value = querySnapshot.docs[querySnapshot.docs.length - 1]
    
    if (isInitial) {
      reviews.value = newReviews
    } else {
      reviews.value = [...reviews.value, ...newReviews]
    }

    hasMoreReviews.value = querySnapshot.docs.length === PAGE_SIZE

  } catch (err) {
    console.error('加载评论失败:', err)
    error.value = err
    showNotification('error', '加载评论失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

// 提交评论
const submitReview = async () => {
  if (isSubmitting.value) return
  
  if (!newReview.value.rating) {
    showNotification('error', '请选择评分')
    return
  }

  if (!newReview.value.comment.trim()) {
    showNotification('error', '请输入评论内容')
    return
  }

  try {
    isSubmitting.value = true
    const { db } = await ensureFirebaseInitialized()
    
    if (!db) {
      throw new Error('Firebase未初始化')
    }

    const reviewsRef = collection(db, 'reviews')
    await addDoc(reviewsRef, {
      rating: newReview.value.rating,
      content: newReview.value.comment.trim(),
      createdAt: serverTimestamp()
    })

    // 重置表单
    newReview.value = {
      rating: 0,
      comment: ''
    }

    // 重新加载评论
    await loadReviews(true)
    showNotification('success', '评论提交成功')

  } catch (err) {
    console.error('提交评论失败:', err)
    showNotification('error', '提交评论失败，请稍后重试')
  } finally {
    isSubmitting.value = false
  }
}

// 加载更多评论
const loadMoreReviews = async () => {
  if (loading.value || !hasMoreReviews.value) return
  await loadReviews()
}

// 初始化
onMounted(async () => {
  try {
    await loadReviews(true)
  } catch (err) {
    console.error('初始化评论失败:', err)
    showNotification('error', '加载评论失败，请刷新页面重试')
  }
})

// 计算可见评论
const visibleReviews = computed(() => {
  return reviews.value.map((review, index) => ({
    ...review,
    offsetY: index * REVIEW_HEIGHT
  }))
})
</script>

<style scoped>
.review-container {
  @apply max-w-6xl mx-auto px-4 md:px-8 py-16;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
}

.star-button {
  @apply text-5xl focus:outline-none transform transition-all duration-500;
}

.star-button:hover {
  @apply scale-125;
  filter: drop-shadow(0 0 12px rgba(251, 191, 36, 0.6));
}

.submit-button {
  @apply px-12 py-5 text-lg font-medium rounded-xl relative overflow-hidden transition-all duration-500;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
}

.submit-button:not(:disabled):hover {
  @apply transform scale-105;
  box-shadow: 0 20px 40px -10px rgba(79, 70, 229, 0.5);
}

.review-card {
  margin-bottom: 16px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.review-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.text-yellow-400 {
  color: #fbbf24;
  text-shadow: 0 0 15px rgba(251, 191, 36, 0.4);
}

@keyframes star-bounce {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.3); }
}

.star-button:hover {
  animation: star-bounce 0.8s cubic-bezier(0.36, 0, 0.66, -0.56) infinite;
}

/* 通知动画 */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-enter-from,
.notification-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}

.review-list {
  position: relative;
  height: 600px; /* 减小列表高度 */
  overflow-y: auto;
  padding: 20px;
  margin: -20px;
}

/* 自定义滚动条 */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(243, 244, 246, 0.3);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(107, 114, 128, 0.2);
  border-radius: 3px;
  transition: all 0.3s;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(107, 114, 128, 0.4);
}

/* 淡入动画 */
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.5s ease-out forwards;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .review-list {
    height: 500px;
    padding: 16px;
    margin: -16px;
  }
  
  .review-card {
    padding: 16px;
  }
}
</style> 